import { type MutableRefObject, useState, useEffect } from 'react'

/**
 * A React hook that detects when a DOM element enters or leaves the viewport
 * using the Intersection Observer API.
 */

export function useIsInView({
  ref,
  options = { threshold: 1, root: null, rootMargin: undefined },
}: {
  ref: MutableRefObject<HTMLDivElement | null>
  options?: IntersectionObserverInit | undefined
}): { isInView: boolean } {
  const [isInView, setIsInView] = useState<boolean>(false)

  const callbackFunction = (entries: IntersectionObserverEntry[]): void => {
    const [entry] = entries
    setIsInView(entry.isIntersecting)
  }

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFunction, options)

    const container = ref.current

    if (container) observer.observe(container)

    return () => {
      if (container) observer.unobserve(container)
    }
  }, [ref, options])

  return {
    isInView,
  }
}
